import React, { useEffect, useLayoutEffect, useState } from "react";
import clsx from "clsx";
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useParams } from "react-router-dom";
import { Table, Space, Form, Input, Checkbox, Spin } from "antd";
import { Helmet as HelmetAsync } from "react-helmet-async";
import styled from "styled-components";
import { useIntl } from "react-intl";
import {
    useAppDispatch,
    useAppSelector
} from "../../redux/store";
import StyledText from "../../components/StyledText";
import {
    programDataSelector,
    guestLoginObjectSelector,
    actionInteractionVotesSelector,
    errorSelector
} from "../../redux/selectors";
import {
    getActionInteraction,
    getNewToken,
    getProgramById,
} from "../../redux/actions";
import { formatDate, getLocalStorageItem, setLocalStorageItem } from "../../utils/utilities";
import Tag from "../../components/Tag";
import CustomModal from "../../components/Modal";
import Button from "../../components/Button";
import LinkComponent from "../../components/LinkComponent";

const PreApprovedAction = ({ className }) => {
    const intl = useIntl();
    const [isLoading, setIsLoading] = useState(false);
    const loginObj = useAppSelector(guestLoginObjectSelector);
    const [votingPanelModal, setVotingPanelModal] = useState(true);
    const baseClassName = clsx("applicationAction", className);
    const [allInteractions, setAllInteractions] = useState([]);
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const votes = useAppSelector(actionInteractionVotesSelector);
    const programData = useAppSelector(programDataSelector) || {};
    const navigate = useNavigate();
    const decodeToken = (getLocalStorageItem('id_Token') && jwtDecode(getLocalStorageItem('id_Token'))) || {};
    const token = getLocalStorageItem('id_Token') || '';
    const userType = getLocalStorageItem('userType') || '';
    const { uniqueKey, linkExpiryKey, progId, email, accessCode } = useParams()
    const savedProgramId = sessionStorage.getItem('savedProgramId');
    const savedRoundId = sessionStorage.getItem('savedRoundId');
    const { voterEmail, companyID, uniqueKey: savedUniqueKey, voterName } = JSON.parse(sessionStorage.getItem('sharePreApprovedData')) || {};
    const { privacyPolicyURL, companyName } = programData || {};
    const { message: errorMessage } = useAppSelector(errorSelector) || {};
    const { IDToken: idToken } = loginObj || {};
    const tokenData = getLocalStorageItem('id_Token');
    const IDToken = idToken || tokenData || '';
    useLayoutEffect(() => {
        if (token && userType !== 'Guest') {
            localStorage.clear();
        }
    }, [token, userType]);

    useEffect(() => {
        if (!voterName) {
            sessionStorage.setItem('sharePreApprovedData', JSON.stringify({
                voterEmail: email,
                programID: progId,
                uniqueKey,
                linkExpiryKey,
                accessCode
            }))
        }
    }, [voterName])

    useEffect(() => {
        if (["Unauthorized! Please log in again", "link expired"]?.includes(errorMessage) && uniqueKey) {
            navigate(`/share/verification/${uniqueKey}`);
        }
    }, [errorMessage, uniqueKey])

    useEffect(() => {
        if (IDToken) {
            const decodedToken = jwtDecode(IDToken);
            setLocalStorageItem('userType', decodedToken["custom:entity"]);
            setLocalStorageItem('companyID', decodedToken["custom:entityID"]);
            setLocalStorageItem('id_Token', IDToken);
            setLocalStorageItem('user_details', decodedToken["email"]);
        }
    }, [IDToken, dispatch]);

    const getAllInteractions = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const fetchSecuredUrls = async () => {
                const updatedInteractions = await Promise.all(
                    votes?.map(async (item) => {
                        try {
                            return { ...item };
                        } catch (error) {
                            console.error('Error fetching secured URL:', error);
                            return item;
                        }
                    })
                );
                setAllInteractions(updatedInteractions);
            };

            await fetchSecuredUrls();
        } catch (error) {
            console.error('Error in getAllInteractions:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (linkExpiryKey && accessCode && savedUniqueKey) {
            if (savedUniqueKey && !voterName && !IDToken) {
                dispatch(getNewToken(`accessCode=${accessCode}&linkExpiryKey=${linkExpiryKey}`));
            }
            if (IDToken) {
                setTimeout(() => {
                    dispatch(getActionInteraction({
                        sortBy: "programName",
                        asc: "true",
                        filterBy: {
                            companyID,
                            programID: progId,
                            status: [
                                "PreApproved"
                            ],
                        },
                        guestEmail: voterEmail,
                        limit: "100"
                    }));
                }, 0)
            }
        }
    }, [accessCode, linkExpiryKey, IDToken, voterEmail, voterName]);


    useEffect(() => {
        if (progId && savedUniqueKey && IDToken) {
            document.documentElement.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            dispatch(getProgramById(progId));
        }
    }, [progId, savedUniqueKey, IDToken]);

    useEffect(() => {
        if (votes?.length) {
            getAllInteractions()
        } else if (Array.isArray(votes) && !votes?.length) {
            setAllInteractions(null);
        }
    }, [votes]);

    const getTableData = (list) => {
        if (list?.length > 0) {
            return list?.map((item, idx) => {
                const {
                    programName,
                    charityName,
                    opportunityName,
                    updateDate,
                    status,
                    userID,
                    ID,
                    votes,
                    programID,
                    opportunityID
                } = item || {};
                return {
                    key: idx,
                    programName,
                    charityName,
                    opportunityName,
                    updateDate: formatDate(updateDate),
                    status,
                    userID,
                    ID,
                    votes,
                    programID,
                    opportunityID
                };
            });
        }
        return null;
    }

    const handleSort = (sortId) => {
        if (['programName', 'charityName', 'opportunityName', 'updateDate']?.includes(sortId.key)) {
            dispatch(getActionInteraction({
                sortBy: sortId?.key,
                asc: "true",
                filterBy: {
                    status: [
                        "PreApproved"
                    ],
                    ...(savedProgramId !== "All" && { programID: savedProgramId, companyID: decodeToken?.companyID, currentBudgetEndDate: savedRoundId || undefined })
                }
            }));
        }
    }

    const getColumns = (voter) => [
        {
            title: intl.formatMessage({ id: "programme" }),
            dataIndex: 'programName',
            key: 'programName',
            render: (_, { programName }) => (
                <div className="nameContainer">
                    <div className="blockWithText">
                        {programName}
                    </div>
                </div>
            ),
            onHeaderCell: (column) => {
                return {
                    onClick: () => handleSort(column),
                };
            },
            sorter: () => { },
        },
        {
            title: intl.formatMessage({ id: "entity" }),
            dataIndex: 'charityName',
            key: 'charityName',
            onHeaderCell: (column) => {
                return {
                    onClick: () => handleSort(column),
                };
            },
            sorter: () => { },
        },
        {
            title: intl.formatMessage({ id: "opportunity" }),
            dataIndex: 'opportunityName',
            key: 'opportunityName',
            onHeaderCell: (column) => {
                return {
                    onClick: () => handleSort(column),
                };
            },
            sorter: () => { },
        },
        {
            title: intl.formatMessage({ id: "date_applied" }),
            dataIndex: 'updateDate',
            key: 'updateDate',
            onHeaderCell: (column) => {
                return {
                    onClick: () => handleSort(column),
                };
            },
            sorter: () => { },
        },
        {
            title: intl.formatMessage({ id: "status" }),
            key: 'status',
            dataIndex: 'status',
            render: (_, { status }) => {
                return (<Tag key={status} type='admin'>
                    {intl.formatMessage({ id: "pre_approved_caps" })?.toUpperCase()}
                </Tag>)
            }
            ,
        },
        {
            title: intl.formatMessage({ id: "my_vote" }),
            dataIndex: 'sumVoterVote',
            key: 'sumVoterVote',
            render: (_, { votes }) => {
                return (<span>{votes?.sumVoterVote}</span>)
            }
        },
        {
            title: intl.formatMessage({ id: "actions" }),
            key: 'actions',
            dataIndex: 'status',
            render: (_, { programID, opportunityID }) => (
                <Space size="middle">
                    <button className='button' onClick={() => navigate(`/company/applications/preapproved/${programID}/${opportunityID}`)
                    }>{intl.formatMessage({ id: "review" })}</button>
                </Space>
            ),
        },
    ];

    const onFinish = (values) => {
        const formattedTime = dayjs().format('YYYYMMDD HH:mm:ss');
        const existingData = JSON.parse(sessionStorage.getItem('sharePreApprovedData')) || {};

        const updatedData = {
            ...existingData,
            voterName: values?.voterName,
            termsApprovedTime: formattedTime,
            termsApproved: 'Y'
        };
        sessionStorage.setItem('sharePreApprovedData', JSON.stringify(updatedData));
        setVotingPanelModal(false)
    };

    const isValid = Array.isArray(allInteractions) && allInteractions?.length === 0;

    return (
        <div className={baseClassName}>
            <HelmetAsync>
                <title>Applications Page</title>
            </HelmetAsync>
            {!companyName && (
                <div className="overlay">
                    <Spin className="spinner" size="large" spinning={!companyName} />
                </div>
            )}
            {voterName && <>
                <StyledText as="p" variant="H1" className="title">
                    {intl.formatMessage({ id: "applications" })}
                </StyledText>
                <div className='subContentContainer'>
                    <Table columns={getColumns(allInteractions)} loading={isValid} dataSource={getTableData(allInteractions)} />
                </div>
            </>}
            <CustomModal
                title={intl.formatMessage({ id: 'welcome_to_your_voting_panel' })}
                open={votingPanelModal && !voterName && companyName}
                onOk={() => setVotingPanelModal(false)}
                onCancel={() => setVotingPanelModal(false)}
                closable={false}
                maskClosable={false}
                footer={null}>
                <StyledText as="span" variant="B3">{intl.formatMessage({ id: "please_click_review_to_assess_each_application_and_then_cast_your_vote" })}</StyledText>
                <Form
                    form={form}
                    className="formContainer"
                    name="register"
                    onFinish={onFinish}
                    scrollToFirstError
                >

                    <Form.Item
                        name="voterName"
                        className="formItem"
                        rules={[
                            { required: true, message: intl.formatMessage({ id: "enter" }) },
                        ]}
                    >
                        <Input
                            placeholder={intl.formatMessage({ id: "please_enter_your_name" })}
                            type="text"
                        />
                    </Form.Item>
                    <div className="checkBoxWrapperModal">
                        <Form.Item
                            valuePropName="checked"
                            name="user_terms"
                            rules={[
                                {
                                    required: true,
                                    message: intl.formatMessage({ id: 'please_accept_the_user_terms' }),
                                },
                            ]}
                        >
                            <Checkbox />
                        </Form.Item>
                        <LinkComponent href="https://bizgiveworld.com/user-terms/">
                            <StyledText as="p" variant="B3" className='terms'>
                                {intl.formatMessage({ id: "i_accept_the" })}
                                <StyledText as="span" variant="B3"
                                    href="https://bizgiveworld.com/user-terms/"
                                    target="_blank"
                                    rel="noreferrer"
                                    className='companyLinks'
                                >
                                    {intl.formatMessage({ id: "user_terms" })}
                                </StyledText>
                            </StyledText>
                        </LinkComponent>
                    </div>
                    <Form.Item
                        name="privacyPolicyConfirmation"
                        valuePropName="checked"
                        rules={[{ required: true, message: intl.formatMessage({ id: 'please_select' }) }]}
                    >
                        <Checkbox>
                            <StyledText as="span" variant="B3">
                                <LinkComponent href={`https://${privacyPolicyURL}`} className='acknowledgement'><StyledText as="span" variant="B3">
                                    {`${intl.formatMessage({ id: "i_accept_the" })} ${companyName} `}
                                </StyledText>
                                    <StyledText as="span" variant="B3b">
                                        {` ${intl.formatMessage({ id: 'privacy_policy' })}`}
                                    </StyledText></LinkComponent>
                            </StyledText>
                        </Checkbox>
                    </Form.Item>
                    <div className='modalFooterButtonCenter'>
                        <Button
                            variant="primary"
                            htmlType="submit"
                            className='deleteButton'>
                            {intl.formatMessage({ id: "enter" })}
                        </Button>
                    </div>
                </Form>
            </CustomModal>
        </div>
    );
};

const StyledPreApprovedAction = styled(PreApprovedAction)`
  &.applicationAction {
    background: ${(props) => props.theme.backgroundCanvas};
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    margin-top: 50px;
    gap: 20px;
    padding: 30px;
    .nameContainer{
      display: flex;
      gap: 10px;
    }
    .ant-tabs-tab-active {
      .title {
        color: ${(props) => props.theme.bizGiveThemeColor} !important;
      }
    }
    .ant-tabs-ink-bar {
      background: ${(props) => props.theme.bizGiveThemeColor} !important;
    }
    .button{
        border: 0;
        background: transparent;
        color: #5550F7;
        cursor: pointer;
        min-width: 100px;
    }
    .checkBoxWrapper{
            display: flex;
            align-items: center;
            gap: 10px;
            margin-top: 10px;
            position: relative;
            .terms{
                position: absolute;
                left: 25px;
                top: 5px;
            }
            .ant-form-item{
                margin-bottom: 0;
            }       
            .companyLinks {
              margin-left: 5px;
              text-decoration: underline;
              color: #04ac9c;
            }
          }
  }
`;

export default StyledPreApprovedAction;
